import { memo, useMemo } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { isNull, noop, sortBy, sum, values as _values } from 'lodash';
// Import Store
import { useAppSelector } from 'store';
import { selectCustomerJourney } from 'store/slices/customerJourney';
// Import UI components
import { BusyLoader, Empty, ExtendedInput, Icon, Paper, Scrollbar, Tag } from '@geneui/components';
// Import Components
import HolderWrapper from 'components/common/HolderWrapper';
import BlockGroup from '../BlockGroup';
// Import Constants
import { l } from 'constants/common';
// Import Helpers
import { getCJBlockTitleLabel } from 'services/Helpers';
import { isBlockGroupReadOnly } from './helper';
// Import Validations
import { flowEditorSidebarValidationSchema } from './validation';
// Import Styles
import styles from './flowEditorSidebar.module.scss';

interface FlowSidebarFormValues {
    search: string;
    selectedGroups: Nullable<string>;
}

interface Props {
    isDisabled?: boolean;
    groupBlocks?: AnyObject[];
    defaultOpened?: boolean;
}

const FlowEditorSidebar: React.FC<Props> = ({ isDisabled = true, groupBlocks = [], defaultOpened = false }) => {
    const { t } = useTranslation();
    const { blocksCounts, maxBlocksCounts } = useAppSelector(selectCustomerJourney);

    const usedBlocksCount = useMemo(() => sum(_values(blocksCounts)), [blocksCounts]);

    const { values, handleChange, handleBlur } = useFormik<FlowSidebarFormValues>({
        initialValues: {
            search: '',
            selectedGroups: null,
        },
        validationSchema: flowEditorSidebarValidationSchema,
        onSubmit: noop,
    });

    const sortedGroups = useMemo(() => sortBy(groupBlocks, ({ orderKey }) => orderKey), [groupBlocks]);

    const filteredGroup = useMemo(() => {
        const tmpSorted = sortedGroups.map(({ blocks, ...restData }): AnyObject => {
            const tmpBlocks = blocks.filter(
                ({ CustomAttributes, Name }) =>
                    !(CustomAttributes?.Hidden || CustomAttributes?.Deprecated) &&
                    (!values.search.trim() ||
                        t(getCJBlockTitleLabel(Name)).toLowerCase().includes(values.search.trim().toLowerCase())),
            );
            return {
                blocks: tmpBlocks,
                ...restData,
            };
        });

        return tmpSorted.filter(({ blocks }) => blocks.length);
    }, [sortedGroups, t, values.search]);

    const groupOptions = useMemo(() => {
        const tmpGroupOptions = sortedGroups.map(({ titleKey, blocksTypes }) => ({
            label: t(titleKey),
            value: titleKey,
            isDisabled: isBlockGroupReadOnly(blocksTypes, blocksCounts, maxBlocksCounts),
        }));

        return tmpGroupOptions;
    }, [blocksCounts, maxBlocksCounts, sortedGroups, t]);

    const finalGroupBlocks = useMemo(() => {
        return filteredGroup.filter(
            ({ titleKey }) => isNull(values.selectedGroups) || values.selectedGroups === titleKey,
        );
    }, [filteredGroup, values.selectedGroups]);

    const toggleGroupBlocksVisibility = (groupKey: string) => () => {
        handleChange({
            target: {
                name: 'selectedGroups',
                value: values.selectedGroups !== groupKey ? groupKey : null,
            },
        });
    };

    return (
        <HolderWrapper
            isDisabled={isDisabled}
            title=""
            position="left"
            footer=""
            expandText={t(l.Expand)}
            collapseText={t(l.Collapse)}
            defaultOpened={defaultOpened}
            containerClassName={styles.holderContainer}
            className={styles.container}
            filterIconType="bc-icon-arrow-right"
        >
            <BusyLoader isBusy={defaultOpened && isDisabled} type="spinner" spinnerSize="big">
                <>
                    <Paper className={styles.searchContainer}>
                        <ExtendedInput
                            name="search"
                            title={t(l.SearchBlocks)}
                            label={t(l.SearchBlocks)}
                            placeholder={t(l.SearchBlocks)}
                            labelAppearance="swap"
                            value={values.search}
                            startAdornment={<Icon type="bc-icon-search" />}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            canClear={true}
                        />
                        <div className={styles.groupOptionsContainer}>
                            {groupOptions.map(({ label, value, isDisabled }) => (
                                <Tag
                                    key={value}
                                    name={label}
                                    cornerRadius="full-radius"
                                    appearance="simple"
                                    // cursor="pointer"
                                    className={styles.groupOptionTag}
                                    selected={values.selectedGroups === value}
                                    onClick={toggleGroupBlocksVisibility(value)}
                                    disabled={isDisabled}
                                />
                            ))}
                        </div>
                    </Paper>
                    <Scrollbar autoHeight={false}>
                        <div className="crm-flow-sidebar-block-groups-wrapper">
                            {finalGroupBlocks.length === 0 ? (
                                <Empty title={t(l.NoDataToDisplay)} />
                            ) : (
                                finalGroupBlocks.map(
                                    ({ titleKey, blocks, blocksTypes, cssClassName, defaultBlockIcon }, index) => (
                                        <BlockGroup
                                            key={index}
                                            titleKey={titleKey}
                                            blocks={blocks}
                                            blocksTypes={blocksTypes}
                                            cssClassName={cssClassName}
                                            defaultBlockIcon={defaultBlockIcon}
                                            isReadOnly={isBlockGroupReadOnly(
                                                blocksTypes,
                                                blocksCounts,
                                                maxBlocksCounts,
                                            )}
                                        />
                                    ),
                                )
                            )}
                        </div>
                    </Scrollbar>

                    {maxBlocksCounts > 0 && (
                        <div className={styles.blocksCountContainer}>
                            {`${t(l.BlocksUsed)} ${usedBlocksCount}/${maxBlocksCounts}`}
                        </div>
                    )}
                </>
            </BusyLoader>
        </HolderWrapper>
    );
};

export default memo(FlowEditorSidebar);
